import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import AddBusinessOutlinedIcon from "@mui/icons-material/AddBusinessOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import CloudSyncOutlinedIcon from "@mui/icons-material/CloudSyncOutlined";
import { NavItem, NavSection } from "../ui/types";

/**
 * Admin Console navigation.
 *
 * Grouped by the permission that unlocks each area, so what a person sees is
 * exactly what they can do. Nothing here keys off a role NAME — `viewer`,
 * `editor` and `admin` are presets, and a custom group holding the same
 * permissions gets the same console.
 *
 * `manage_users` used to gate organisation config as well as users, so an admin
 * without user management could not reach their own org's settings. That is now
 * `manage_configuration`, and bulk imports are `bulk_api_data_import`.
 */
export const buildAdminNav = ({
  isSuperuser,
  isStaff,
  canManageUsers = false,
  canManageConfiguration = false,
  canManageRestrictions = false,
  canBulkApiDataImport = false
}: {
  isSuperuser: boolean;
  isStaff: boolean;
  /** Users and Activity — both are user data. */
  canManageUsers?: boolean;
  /** Organisation config, branding, boundary, API keys — the tenant's own settings. */
  canManageConfiguration?: boolean;
  /** Restriction definitions and assignment. */
  canManageRestrictions?: boolean;
  /** Fulcrum and other bulk API pulls. Kept out of the everyday config area. */
  canBulkApiDataImport?: boolean;
}): NavSection[] => {
  // Superusers get the cross-tenant "Organisations" surface (list all, create,
  // edit any), which absorbs the config link. Org admins instead go straight to
  // their own org's config — they have no org list and no selector.
  const orgItem: NavItem | null = isSuperuser
    ? {
        label: "Organisations",
        icon: <AddBusinessOutlinedIcon fontSize='small' />,
        path: "/admin/organisations",
        match: (p) => p.startsWith("/admin/organisations") || p.startsWith("/admin/config") || p.startsWith("/admin/provisioning")
      }
    : canManageConfiguration
      ? {
          label: "Organisation config",
          icon: <TuneOutlinedIcon fontSize='small' />,
          path: "/admin/config",
          match: (p) => p.startsWith("/admin/config")
        }
      : null;

  const organisationItems: NavItem[] = [
    ...(orgItem ? [orgItem] : []),
    ...(canManageConfiguration
      ? [
          {
            // A key reads the organisation's data and outlives whoever minted
            // it, so it sits with the org's settings rather than with Users.
            label: "API keys",
            icon: <VpnKeyOutlinedIcon fontSize='small' />,
            path: "/admin/api-keys",
            match: (p: string) => p.startsWith("/admin/api-keys")
          }
        ]
      : [])
  ];

  const peopleItems: NavItem[] = canManageUsers
    ? [
        {
          label: "Users",
          icon: <GroupOutlinedIcon fontSize='small' />,
          path: "/admin/users",
          match: (p: string) => p.startsWith("/admin/users")
        },
        {
          // Contribution counts and last-login — user data, so it follows users.
          label: "Activity",
          icon: <InsightsOutlinedIcon fontSize='small' />,
          path: "/admin/activity",
          match: (p: string) => p.startsWith("/admin/activity")
        }
      ]
    : [];

  const accessItems: NavItem[] = canManageRestrictions
    ? [
        {
          label: "Restrictions",
          icon: <LockOutlinedIcon fontSize='small' />,
          path: "/admin/restrictions",
          match: (p: string) => p.startsWith("/admin/restrictions")
        }
      ]
    : [];

  const dataItems: NavItem[] = canBulkApiDataImport
    ? [
        {
          // Deliberately a distinct, clearly-labelled action rather than a panel
          // inside config: a bulk pull is heavy and data-sensitive.
          label: "Bulk import",
          icon: <CloudSyncOutlinedIcon fontSize='small' />,
          path: "/form-import",
          match: (p: string) => p.startsWith("/form-import")
        }
      ]
    : [];

  const platformItems: NavItem[] = [
    ...(isStaff
      ? [
          {
            label: "Developer Tools (legacy)",
            icon: <BuildOutlinedIcon fontSize='small' />,
            path: "/admin/developer-tools",
            match: (p: string) => p.startsWith("/admin/developer-tools")
          }
        ]
      : []),
    { label: "Usage & Costs", icon: <PaidOutlinedIcon fontSize='small' />, comingSoon: true },
    { label: "Data Export", icon: <DownloadOutlinedIcon fontSize='small' />, comingSoon: true }
  ];

  // Only render a section header when that section has something in it —
  // an empty "PEOPLE" heading tells the reader nothing.
  return [
    {
      items: [{ label: "Dashboard", icon: <DashboardOutlinedIcon fontSize='small' />, path: "/admin", match: (p) => p === "/admin" }]
    },
    ...(organisationItems.length ? [{ header: "ORGANISATION", items: organisationItems }] : []),
    ...(peopleItems.length ? [{ header: "PEOPLE", items: peopleItems }] : []),
    ...(accessItems.length ? [{ header: "ACCESS", items: accessItems }] : []),
    ...(dataItems.length ? [{ header: "DATA", items: dataItems }] : []),
    { header: "PLATFORM", items: platformItems }
  ];
};
